// Типизированный REST-клиент. SPEC client-transport §4.2 (один метод на строку таблицы
// shared-contracts §4.5). Формы тел/ответов — ТОЛЬКО типы shared-contracts; локально не
// переобъявляются (инвариант 4). Все вызовы идут через HttpEngine (credentials:'include', CSRF на
// мутациях, единый разбор ErrorEnvelope, 401→onUnauthenticated).
import { uploadTimeoutMs } from './http.js';
/** URL-кодирование path-сегмента (id — UUID, но кодируем на всякий случай). */
function seg(id) {
    return encodeURIComponent(id);
}
/**
 * Создаёт REST-клиент поверх HttpEngine. Пути — канон api-server (REST-DTO §4.5/реконсиляция REST):
 * `/auth/*`, `/users/*`, `/chats/*`, `/messages/*`, `/media/*`, `/receipts/*`, `/search`, `/push/*`,
 * `/admin/*`. SPEC §4.2.
 */
export function createRestClient(http) {
    return {
        // ── auth ──────────────────────────────────────────────────────────────────
        register(b, opts) {
            return http.postJson('/auth/register', b, opts);
        },
        login(b, opts) {
            return http.postJson('/auth/login', b, opts);
        },
        changePassword(b, opts) {
            return http.postJson('/auth/change-password', b, opts);
        },
        logout(opts) {
            return http.post('/auth/logout', opts);
        },
        logoutAll(opts) {
            return http.post('/auth/logout-all', opts);
        },
        me(opts) {
            return http.get('/auth/me', undefined, opts);
        },
        wsTicket(opts) {
            return http.post('/auth/ws-ticket', opts);
        },
        listSessions(opts) {
            return http.get('/auth/sessions', undefined, opts);
        },
        revokeSession(id, opts) {
            return http.deleteJson(`/auth/sessions/${seg(id)}`, undefined, opts);
        },
        // ── users / directory ──────────────────────────────────────────────────────
        listUsers(q, page, opts) {
            return http.get('/users', { q, page }, opts);
        },
        getUser(id, opts) {
            return http.get(`/users/${seg(id)}`, undefined, opts);
        },
        updateMe(patch, opts) {
            return http.patchJson('/users/me', patch, opts);
        },
        setAvatar(file, opts) {
            const form = new FormData();
            form.append('avatar', file);
            // тот же длинный таймаут, что и у аватара группы (мобильный аплинк)
            return http.postForm('/users/me/avatar', form, { timeoutMs: 120_000, ...opts });
        },
        // ── chats ────────────────────────────────────────────────────────────────
        listChats(opts) {
            return http.get('/chats', undefined, opts);
        },
        createDirect(userId, opts) {
            return http.postJson('/chats/direct', { userId }, opts);
        },
        createGroup(b, opts) {
            return http.postJson('/chats/group', b, opts);
        },
        renameChat(id, title, opts) {
            return http.patchJson(`/chats/${seg(id)}`, { title }, opts);
        },
        addMember(id, userId, opts) {
            return http.postJson(`/chats/${seg(id)}/members`, { userId }, opts);
        },
        removeMember(id, userId, opts) {
            return http.deleteJson(`/chats/${seg(id)}/members/${seg(userId)}`, undefined, opts);
        },
        leaveChat(id, opts) {
            return http.post(`/chats/${seg(id)}/leave`, opts);
        },
        getChatMembers(id, opts) {
            return http.get(`/chats/${seg(id)}/members`, undefined, opts);
        },
        setChatAvatar(id, file, opts) {
            const form = new FormData();
            form.append('avatar', file);
            // Фото с телефона по медленному аплинку не влезает в дефолтные 30с — даём загрузке 120с.
            return http.postForm(`/chats/${seg(id)}/avatar`, form, { timeoutMs: 120_000, ...opts });
        },
        deleteChat(id, opts) {
            return http.deleteJson(`/chats/${seg(id)}`, undefined, opts);
        },
        patchChatState(id, patch, opts) {
            return http.patchJson(`/chats/${seg(id)}/state`, patch, opts);
        },
        // ── messages ───────────────────────────────────────────────────────────────
        history(id, o, opts) {
            return http.get(`/chats/${seg(id)}/messages`, { beforeSeq: o?.beforeSeq, limit: o?.limit }, opts);
        },
        sendMessage(id, dto, opts) {
            return http.postJson(`/chats/${seg(id)}/messages`, dto, opts);
        },
        editMessage(id, text, opts) {
            return http.patchJson(`/messages/${seg(id)}`, { text }, opts);
        },
        deleteMessage(id, scope, opts) {
            // scope — В QUERY (`?scope`), как читает api-server (`req.query.scope`). Раньше слался в теле →
            // сервер видел undefined → дефолт 'self': «удалить у всех» молча становилось «у себя» (без
            // deleted_at и без message.deleted-фан-аута), другие клиенты сообщение не теряли.
            return http.deleteJson(`/messages/${seg(id)}?scope=${scope}`, undefined, opts);
        },
        forwardMessage(id, b, opts) {
            return http.postJson(`/messages/${seg(id)}/forward`, b, opts);
        },
        setReaction(id, emoji, opts) {
            return http.postJson(`/messages/${seg(id)}/reactions`, { emoji }, opts);
        },
        unsetReaction(id, emoji, opts) {
            return http.deleteJson(`/messages/${seg(id)}/reactions`, { emoji }, opts);
        },
        // ── media ──────────────────────────────────────────────────────────────────
        initUpload(b, opts) {
            return http.postJson('/media/uploads', b, opts);
        },
        putUploadChunk(uploadId, chunk, range, opts) {
            // PUT /media/uploads/:id с Content-Range (SPEC §4.5). Тело — сырой Blob. Content-Type ВСЕГДА
            // application/octet-stream: чанк — просто байты, а mime вложения уже задан при init. Иначе браузер
            // слал mime файла (напр. image/png), для которого у Fastify нет парсера → 415 «Unsupported Media
            // Type» → загрузка падала. Сервер читает Content-Range, тип чанка ему не важен.
            const contentRange = `bytes ${range.start}-${range.end}/${range.total}`;
            return http.putRaw(`/media/uploads/${seg(uploadId)}`, chunk, { 'Content-Range': contentRange }, 'application/octet-stream', { ...opts, timeoutMs: opts?.timeoutMs ?? uploadTimeoutMs(chunk.size) });
        },
        completeUpload(uploadId, chatId, meta, opts) {
            // chatId — шов R2 W7: сервер требует его на complete (query или тело). Шлём телом.
            // durationMs — заявленная длительность аудио (live-webm без неё в заголовке; сервер хранит в attachments).
            return http.postJson(`/media/uploads/${seg(uploadId)}/complete`, { chatId, ...(typeof meta?.durationMs === 'number' ? { durationMs: meta.durationMs } : {}) }, opts);
        },
        async downloadUrl(attachmentId, opts) {
            // Сервер отвечает JSON `{ url }` (api-server следует 302 → presigned URL внутри себя).
            const r = await http.get(`/media/attachments/${seg(attachmentId)}/download-url`, undefined, opts);
            return r.url;
        },
        downloadBlob(attachmentId, opts) {
            return http.getBlob(`/media/attachments/${seg(attachmentId)}/blob`, opts);
        },
        // ── receipts cold-start (правка I) ──────────────────────────────────────────
        receiptsSnapshot(opts) {
            return http.get('/receipts/snapshot', undefined, opts);
        },
        // ── notify settings ─────────────────────────────────────────────────────────
        getNotifySettings(opts) {
            return http.get('/users/me/notify-settings', undefined, opts);
        },
        updateNotifySettings(patch, opts) {
            return http.patchJson('/users/me/notify-settings', patch, opts);
        },
        // ── search ──────────────────────────────────────────────────────────────────
        search(b, opts) {
            return http.get('/search', { scope: b.scope, q: b.q, type: b.type, chatId: b.chatId }, opts);
        },
        // ── push (зеркало PushClient §4.7) ──────────────────────────────────────────
        pushKey(opts) {
            return http.get('/push/key', undefined, opts);
        },
        pushSubscribe(subscription, opts) {
            return http.postJson('/push/subscribe', subscription, opts);
        },
        pushUnsubscribe(endpoint, opts) {
            return http.postJson('/push/unsubscribe', { endpoint }, opts);
        },
        // ── admin ────────────────────────────────────────────────────────────────────
        adminPending(opts) {
            return http.get('/admin/users/pending', undefined, opts);
        },
        adminListUsers(opts) {
            return http.get('/admin/users', undefined, opts);
        },
        adminApprove(id, opts) {
            return http.post(`/admin/users/${seg(id)}/approve`, opts);
        },
        adminDeactivate(id, opts) {
            return http.post(`/admin/users/${seg(id)}/deactivate`, opts);
        },
        adminDeleteUser(id, opts) {
            return http.deleteJson(`/admin/users/${seg(id)}`, undefined, opts);
        },
        adminSetRole(id, role, opts) {
            return http.postJson(`/admin/users/${seg(id)}/role`, { role }, opts);
        },
        adminResetPassword(id, newPassword, opts) {
            return http.postJson(`/admin/users/${seg(id)}/reset-password`, { newPassword }, opts);
        },
        adminUpdateUser(id, patch, opts) {
            return http.patchJson(`/admin/users/${seg(id)}`, patch, opts);
        },
        adminAudit(page, opts) {
            return http.get('/admin/audit', { page }, opts);
        },
    };
}
/** Push-клиент поверх REST (зеркало §4.7). Отдельная узкая поверхность для pwa-shell. */
export function createPushClient(rest) {
    return {
        async getVapidKey() {
            const r = await rest.pushKey();
            return r.publicKey;
        },
        async subscribe(sub) {
            await rest.pushSubscribe(sub);
        },
        async unsubscribe(endpoint) {
            await rest.pushUnsubscribe(endpoint);
        },
    };
}
//# sourceMappingURL=rest-client.js.map